/**
 * Pure data of the first-launch cold open (port of `FirstLaunchColdOpenView`, AppRootView.swift):
 * the sequence timings, the springs, the caller and the lock-screen notifications per episode.
 */
import type { Translate } from '@/services/i18n';
import { EPISODE_CATALOG } from '@/domain/catalog';

export const JORDAN_EPISODE_ID = 'episode_3_double_booked';

/** `runSequence()` sleeps, in ms. */
export const COLD_OPEN_TIMING = {
  /** Incoming call screen (haptic buzz duration too). */
  incomingCall: 3800,
  /** Lock screen → "3 missed calls" (skipped for Jordan). */
  missedCalls: 880,
  /** → message request (880 for Jordan, who has no missed calls). */
  messageRequest: 1700,
  messageRequestJordan: 880,
  /** → "Open conversation" control. */
  unlockReady: 1050,
  /** `handleUnlock` → `onOpen()`. */
  openDelay: 190,
} as const;

export type ColdOpenStep = 'lockScreen' | 'missedCalls' | 'messageRequest' | 'unlockReady';

/** Absolute schedule (ms from the start of the call) of every step for an episode. */
export function coldOpenSchedule(episodeID: string): { step: ColdOpenStep; at: number }[] {
  const T = COLD_OPEN_TIMING;
  const jordan = episodeID === JORDAN_EPISODE_ID;
  const lock = T.incomingCall;
  const steps: { step: ColdOpenStep; at: number }[] = [{ step: 'lockScreen', at: lock }];
  let at = lock;
  if (!jordan) {
    at += T.missedCalls;
    steps.push({ step: 'missedCalls', at });
  }
  at += jordan ? T.messageRequestJordan : T.messageRequest;
  steps.push({ step: 'messageRequest', at });
  at += T.unlockReady;
  steps.push({ step: 'unlockReady', at });
  return steps;
}

/**
 * SwiftUI `.spring(response:dampingFraction:)` → mass-1 spring (stiffness, damping) for motion.
 * k = (2π / response)², c = 4π·ζ / response.
 */
export function swiftSpring(response: number, dampingFraction: number) {
  return {
    type: 'spring' as const,
    stiffness: (2 * Math.PI / response) ** 2,
    damping: (4 * Math.PI * dampingFraction) / response,
    mass: 1,
  };
}

export const SPRINGS = {
  /** incoming call → lock screen */
  phase: swiftSpring(0.36, 0.88),
  /** notification inserted */
  notification: swiftSpring(0.48, 0.84),
  /** "Open conversation" appears */
  unlockReady: swiftSpring(0.40, 0.86),
  /** slider: snap to the end / back to the start */
  sliderSnap: swiftSpring(0.24, 0.88),
  sliderReturn: swiftSpring(0.32, 0.80),
} as const;

export const PINK = 'rgb(255 79 135)'; // TeaCheckTheme.receiptPink (1.0, 0.31, 0.53)
export const PHONE_GREEN = 'rgb(31 189 87)'; // (0.12, 0.74, 0.34)

export interface ColdOpenNotification {
  id: 'missed_calls' | 'message_request';
  appName: string;
  title: string;
  message: string;
  time: string;
  glyph: 'phone' | 'message';
  tint: string;
  avatarAssetName?: string;
}

export function isJordanOpening(episodeID: string): boolean {
  return episodeID === JORDAN_EPISODE_ID;
}

/** `EpisodeCatalog.entry(for:)?.avatarAssetName`. */
export function clientAvatarAssetName(episodeID: string): string | undefined {
  return EPISODE_CATALOG.find((e) => e.id === episodeID)?.avatarAssetName;
}

export function messageRequestName(episode: { id: string; clientName: string }): string {
  switch (episode.id) {
    case 'episode_1_same_spot_thursday': return 'cindy parker';
    case 'episode_2_read_the_room_babe': return 'maya rivera';
    default: return episode.clientName.toLowerCase();
  }
}

export interface ColdOpenCaller {
  name: string;
  isKnown: boolean;
  avatarAssetName?: string;
}

export function coldOpenCaller(episodeID: string, t: Translate): ColdOpenCaller {
  return isJordanOpening(episodeID)
    ? { name: 'Jordan', isKnown: true, avatarAssetName: clientAvatarAssetName(episodeID) }
    : { name: t('no caller id'), isKnown: false };
}

/** Every notification of the episode, in insertion order (the list shows them newest first). */
export function coldOpenNotifications(episode: { id: string; clientName: string }, t: Translate): ColdOpenNotification[] {
  const avatar = clientAvatarAssetName(episode.id);
  if (isJordanOpening(episode.id)) {
    return [{
      id: 'message_request', appName: t('chat'), title: 'Jordan', message: t('She just left. Are you there?'),
      time: t('now'), glyph: 'message', tint: PINK, avatarAssetName: avatar,
    }];
  }
  return [
    { id: 'missed_calls', appName: t('phone'), title: t('3 missed calls'), message: t('no caller id'), time: t('now'), glyph: 'phone', tint: PHONE_GREEN },
    {
      id: 'message_request', appName: t('chat'), title: t('message request'), message: t('%@ wants to chat', [messageRequestName(episode)]),
      time: t('now'), glyph: 'message', tint: PINK, avatarAssetName: avatar,
    },
  ];
}
